import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AccountBalanceWallet as WalletIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import { BALANCE_ANCHOR_ID, formatDueDate, formatMoney, invoiceLabel } from '../../utils/outstandingBalance';

export const InvoiceDetails = ({ invoice, actionLabel = 'Pay Now' }) => (
  <Paper variant="outlined" sx={{ p: 2 }} data-testid="outstanding-invoice">
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {invoiceLabel(invoice)}
          </Typography>
          <Chip
            label={`Overdue · ${invoice.daysOverdue} day${invoice.daysOverdue === 1 ? '' : 's'}`}
            color="error"
            size="small"
            clickable={false}
            onClick={() => {}}
          />
        </Stack>
        {invoice.invoiceNumber && (
          <Typography variant="body2" color="text.secondary">
            Invoice #{invoice.invoiceNumber}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Due {formatDueDate(invoice.dueDate)}
        </Typography>
        {invoice.description && (
          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {invoice.description}
          </Typography>
        )}
        {invoice.lineItems?.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {invoice.lineItems.map((item, index) => (
              <Typography component="li" variant="body2" key={index} sx={{ wordBreak: 'break-word' }}>
                {item}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          {formatMoney(invoice.amountDueCents)}
        </Typography>
        {invoice.publicUrl && (
          <Button
            variant="contained"
            color="error"
            href={invoice.publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
          >
            {actionLabel}
          </Button>
        )}
      </Stack>
    </Stack>
  </Paper>
);

/**
 * Overdue invoices on the player profile. The parent decides visibility; the API enforces it too
 * (self, Super/Tenant Admin, Reporter, Spares Coordinator). Hidden when nothing is owed.
 */
const OutstandingBalanceSection = ({ userTenantId, isOwnProfile }) => {
  const sectionRef = useRef(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBalance(null);
    (async () => {
      try {
        const data = isOwnProfile
          ? await unifiedApi.getMyBalance()
          : await unifiedApi.getMemberBalance(userTenantId);
        if (!cancelled) setBalance(data || null);
      } catch {
        // Forbidden or failed — don't surface the section.
        if (!cancelled) setBalance(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userTenantId, isOwnProfile]);

  const total = balance?.totalOverdueCents || 0;

  useEffect(() => {
    if (!loading && total > 0 && window.location.hash === `#${BALANCE_ANCHOR_ID}` && sectionRef.current?.scrollIntoView) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, total]);

  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (total <= 0) return null;

  return (
    <Box id={BALANCE_ANCHOR_ID} ref={sectionRef} sx={{ mt: 3, scrollMarginTop: 80 }} data-testid="outstanding-balance-section">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <WalletIcon sx={{ mr: 1 }} color="error" />
          Outstanding Balance
        </Typography>
        <Typography variant="h6" color="error.main">
          {formatMoney(total)}
        </Typography>
      </Stack>
      <Stack spacing={1.5}>
        {balance.invoices.map((invoice) => (
          <InvoiceDetails
            key={invoice.invoiceId}
            invoice={invoice}
            actionLabel={isOwnProfile ? 'Pay Now' : 'Open Invoice'}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default OutstandingBalanceSection;
